import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import usePageTitle from '../../hooks/usePageTitle';
import { API_URL } from '../../config';

// hooks
import useFormEvents from '../../hooks/useFormEvents';

// components
import AuthBrandCanvas from '../../components/Auth/AuthBrandCanvas';

// styles
import '../../styles/auth.css';

interface IFormProps {
  email: string;
}

const ForgotScreen: React.FC = () => {
  usePageTitle('Mot de passe oublié — KaneaBourse');
  const { onlyEmail } = useFormEvents();

  const [formValues, setFormValues] = useState<IFormProps>({
    email: '',
  });

  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formValues.email }),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      const data = await response.json();
      setMessage(data.message || 'Si cette adresse existe, un lien de réinitialisation a été envoyé.');
    } catch (err) {
      setError('Une erreur s\'est produite. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <div className="auth-panel">

        {/* ── Panneau gauche branding ── */}
        <div className="auth-brand-panel">
          <AuthBrandCanvas />
          <div className="auth-brand-spotlight" />

          <div className="auth-brand-logo">
            <div className="auth-brand-logo-img-wrap">
              <KaneaLogo variant="mark" title={null} />
            </div>
            <span className="auth-brand-name">KaneaBourse</span>
            <div className="auth-brand-logo-dot" />
          </div>

          <div className="auth-brand-body">
            <h2>
              N'investissez plus sur des <em>rumeurs</em>
            </h2>
            <p>
              KaneaBourse analyse froidement les bilans officiels BRVM —
              14 modèles de valorisation, indicateurs techniques, Nia votre copilote en français.
              Analysez ici, passez vos ordres chez votre SGI.
            </p>
            <div className="auth-brand-stats">
              <div>
                <div className="auth-brand-stat-value">10s</div>
                <div className="auth-brand-stat-label">Pour décider</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">48</div>
                <div className="auth-brand-stat-label">Sociétés suivies</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">24/7</div>
                <div className="auth-brand-stat-label">Copilote Nia</div>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            © {new Date().getFullYear()} KaneaBourse · Données EOD BRVM
          </div>
        </div>

        {/* ── Panneau droit formulaire ── */}
        <div className="auth-form-panel">
          <div className="auth-form-inner">

            {/* ── Logo Mobile ── */}
            <div className="auth-mobile-logo">
              <KaneaLogo variant="mark" title={null} />
              <span className="auth-mobile-logo-text">KaneaBourse</span>
            </div>

            <div className="auth-form-header">
              <h1>Mot de passe oublié ? 🔑</h1>
              <p>Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.</p>
            </div>

            {message && (
              <div className="auth-success" style={{
                padding: '12px 16px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '8px',
                color: '#34d399',
                fontSize: '0.875rem',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontWeight: 'bold' }}>✓</span> {message}
              </div>
            )}

            {error && (
              <div className="auth-error">
                <span>⚠</span> {error}
              </div>
            )}

            <form noValidate onSubmit={handleSubmit}>

              <div className="auth-field">
                <label htmlFor="email">Adresse email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  onKeyDown={onlyEmail}
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                className="auth-btn-primary"
                disabled={loading}
                style={{ marginTop: 8 }}
              >
                {loading ? 'Envoi en cours…' : 'Envoyer le lien →'}
              </button>

              <Link to="/login" className="auth-btn-secondary">
                Se connecter
              </Link>

              <div className="auth-form-link">
                <Link to="/members/signup">Pas encore de compte ? S'inscrire gratuitement</Link>
              </div>

            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ForgotScreen;
